import { IHttpAdapter } from '../../entity/interface/iHttpAdapter';
import {
    IActiveSubscriptionRedeeemGiftCardResponse,
    IGiftCardUserResubscribeReq,
    IGiftCardUserResubscribeResponse,
    IGiftCardUserResubscribeVerifyReq,
    IGiftCardUserResubscribeVerifyResponse,
    IReSubscribeViaGiftCardResponse,
    ISubscribeViaGiftCardResponse,
    IValidateGiftCardResponse,
} from '../../entity/interface/iDiscounts';
import { CSRFTokenRequest } from '../../entity';
import { IDiscountRepository } from '../../core/repositories/interfaces/iDiscountRepository';
import { API_IDS } from '@intigral/constant';
import { container } from '../../DataDomain';
import { IApiPathManager } from '../config/ApiPathManager';
import { DI } from '@intigral/constant/config/DIContainer';

export class DiscountRepository implements IDiscountRepository {
    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }
    async validateGiftCard(
        card_number: string,
        event_type: string,
        csrfToken: CSRFTokenRequest
    ): Promise<IValidateGiftCardResponse> {
        const response = await this.http.post<IValidateGiftCardResponse>(
            this.apiPathManager.getPath(API_IDS.DISCOUNT_API_VALIDATE_GIFT_CARD),
            {
                withCredentials: true,
                body: {
                    card_number,
                    event_type,
                    ...csrfToken,
                },
            }
        );
        return response.data;
    }

    async subscribeViaGiftCard(
        discountCode: string,
        username: string,
        lang: string,
        tierGuid: string,
        password: string,
        csrfToken: CSRFTokenRequest
    ): Promise<ISubscribeViaGiftCardResponse> {
        const response = await this.http.post<ISubscribeViaGiftCardResponse>(
            this.apiPathManager.getPath(API_IDS.DISCOUNT_API_SUBSCRIBE_BY_GIFT_CARD),
            {
                withCredentials: true,
                body: {
                    discountCode,
                    username,
                    lang,
                    tierGuid,
                    password,
                    ...csrfToken,
                },
            }
        );
        return response.data;
    }

    async activeSubscriptionRedeeemGiftCard(
        username: string,
        identityToken: string,
        discountCode: string,
        token: string,
        csrfToken: CSRFTokenRequest
    ): Promise<IActiveSubscriptionRedeeemGiftCardResponse> {
        const response = await this.http.post<IActiveSubscriptionRedeeemGiftCardResponse>(
            this.apiPathManager.getPath(API_IDS.ACTIVE_SUB_VOUCHER),
            {
                withCredentials: true,
                body: {
                    username,
                    identityToken,
                    discountCode,
                    token,
                    ...csrfToken,
                },
            }
        );
        return response.data;
    }

    async reSubscribeViaGiftCard(
        username: string,
        tierGuid: string,
        discountCode: string,
        token: string,
        identityToken: string,
        authSign: string,
        authToken: string,
        csrfToken: CSRFTokenRequest
    ): Promise<IReSubscribeViaGiftCardResponse | null> {
        const response = await this.http.post<IReSubscribeViaGiftCardResponse>(
            this.apiPathManager.getPath(API_IDS.DISCOUNT_API_RESUBSCRIBE_BY_GIFT_CARD),
            {
                withCredentials: true,
                body: {
                    username,
                    tierGuid,
                    discountCode,
                    token,
                    identityToken,
                    authSign,
                    authToken,
                    ...csrfToken,
                },
            }
        );
        return response.data;
    }

    async giftCardUserResubscribe(
        payload: IGiftCardUserResubscribeReq,
        authToken: string,
        authSign: string,
        identityToken: string,
        csrfToken: CSRFTokenRequest
    ): Promise<IGiftCardUserResubscribeResponse> {
        const response = await this.http.post<IGiftCardUserResubscribeResponse>(
            this.apiPathManager.getPath(API_IDS.DISCOUNT_API_GIFT_CARD_USER_RESUBSCRIBE),
            {
                withCredentials: true,
                body: {
                    ...csrfToken,
                    ...payload,
                    authToken,
                    authSign,
                    identityToken,
                },
            }
        );
        return response.data;
    }

    async giftCardUserResubscribeVerify(
        payload: IGiftCardUserResubscribeVerifyReq,
        authToken: string,
        authSign: string,
        identityToken: string,
        csrfToken: CSRFTokenRequest
    ): Promise<IGiftCardUserResubscribeVerifyResponse> {
        const response = await this.http.post<IGiftCardUserResubscribeVerifyResponse>(
            this.apiPathManager.getPath(API_IDS.DISCOUNT_API_GIFT_CARD_USER_RESUBSCRIBE_VERIFY),
            {
                withCredentials: true,
                body: {
                    ...csrfToken,
                    ...payload,
                    authToken,
                    authSign,
                    identityToken,
                },
            }
        );
        return response.data;
    }
}
